/**
 * Pure maths for the Sun's well. No three.js, no DOM: everything here runs in node and is covered by tests/sun.test.ts.
 *
 * Units: Sun radius = 1, GM = 1, c^2 = 8. Gravity is exaggerated about 60,000 times so the bending is
 * visible on screen; the Schwarzschild radius is 0.25.
 */

export const GM = 1;
export const C2 = 8;
export const C = Math.sqrt(C2);
/** Schwarzschild radius, 2GM/c^2 */
export const RS = (2 * GM) / C2;
export const SUN_R = 1;

/** Where the starlight begins, and where it is meant to land. */
export const START_X = -9;
export const EARTH = { x: 6.2, y: -0.75 } as const;
/** how close a ray must pass to Earth's telescope to count as a hit */
export const HIT_RADIUS = 0.55;

export type Physics = 'einstein' | 'newton';
/** sun: the ray ran into the Sun. escaped: it left the region. wound: it went round more than a full turn. */
export type GeodesicFate = 'sun' | 'escaped' | 'wound';

export interface GeodesicOptions {
  /** starting x, default -9 */
  x0?: number;
  /** stop beyond this radius, default 14 */
  rMax?: number;
  /** integration step in the angle phi, default 0.004 */
  dphi?: number;
  /** stop after this much angle, default 2 pi */
  maxPhi?: number;
}

export interface Geodesic {
  /** flat x, y pairs in the ray's own plane, starting at (x0, b) */
  points: number[];
  fate: GeodesicFate;
}

/**
 * Integrate a ray started at (x0, y0) with velocity (vx, vy) in the plane, using the orbit equation
 *   u'' = K + G u^2 - u        (u = 1/r, ' = d/dphi)
 * Einstein: K = 0, G = 3GM/c^2 (the curved-spacetime term).   Newton: K = GM/h^2, G = 0 (a corpuscle pulled by a force).
 * RK4 in the angle. Stops when r < 1 (into the Sun), r > rMax, or the angle passes maxPhi.
 */
export function traceRay(x0: number, y0: number, vx: number, vy: number, physics: Physics, opts: GeodesicOptions = {}): Geodesic {
  const rMax = opts.rMax ?? 14;
  const d = opts.dphi ?? 0.004;
  const maxPhi = opts.maxPhi ?? 2 * Math.PI;
  const r0 = Math.hypot(x0, y0);
  const phi0 = Math.atan2(y0, x0);
  const vr = (x0 * vx + y0 * vy) / r0;
  let vt = (-y0 * vx + x0 * vy) / r0;
  const sgn = vt >= 0 ? 1 : -1;
  vt = Math.max(Math.abs(vt), 1e-9);
  const h = r0 * vt;
  const K = physics === 'newton' ? GM / (h * h) : 0;
  const G = physics === 'einstein' ? (3 * GM) / C2 : 0;
  const f = (u: number) => K + G * u * u - u;

  const points: number[] = [x0, y0];
  let u = 1 / r0;
  let up = -vr / (r0 * vt);
  let phi = 0;
  let fate: GeodesicFate = 'wound';
  if (r0 < SUN_R) return { points, fate: 'sun' };
  if (r0 > rMax) return { points, fate: 'escaped' };

  for (let i = 0; i < 100000 && phi <= maxPhi; i++) {
    const k1u = up, k1p = f(u);
    const k2u = up + 0.5 * d * k1p, k2p = f(u + 0.5 * d * k1u);
    const k3u = up + 0.5 * d * k2p, k3p = f(u + 0.5 * d * k2u);
    const k4u = up + d * k3p, k4p = f(u + d * k3u);
    u += (d / 6) * (k1u + 2 * k2u + 2 * k3u + k4u);
    up += (d / 6) * (k1p + 2 * k2p + 2 * k3p + k4p);
    phi += d;
    if (u <= 0) { fate = 'escaped'; break; }
    const r = 1 / u;
    const a = phi0 + sgn * phi;
    points.push(r * Math.cos(a), r * Math.sin(a));
    if (r < SUN_R) { fate = 'sun'; break; }
    if (r > rMax) { fate = 'escaped'; break; }
  }
  return { points, fate };
}

/**
 * A ray of starlight that begins at (x0, b) moving in +x at speed c, with impact parameter b.
 * Returns a flat array of x, y pairs and how the journey ended.
 */
export function nullGeodesic(b: number, physics: Physics, opts: GeodesicOptions = {}): Geodesic {
  return traceRay(opts.x0 ?? START_X, b, C, 0, physics, opts);
}

/** Weak-field bend of a ray with impact parameter b, 4GM/(b c^2), in radians. A force would give half. */
export function bendRadians(b: number): number { return (4 * GM) / (b * C2); }
export const radToDeg = (r: number) => (r * 180) / Math.PI;

/** Angle in radians between the launch direction (+x) and the last segment of the path. Sign is positive when it turned toward -y. */
export function deflectionAngle(points: number[]): number {
  const n = points.length / 2;
  if (n < 2) return 0;
  const dx = points[2 * n - 2] - points[2 * n - 4];
  const dy = points[2 * n - 1] - points[2 * n - 3];
  return -Math.atan2(dy, dx);
}

/** Total length of a flat x, y polyline. */
export function polylineLength(points: number[]): number {
  let s = 0;
  for (let i = 2; i < points.length; i += 2) s += Math.hypot(points[i] - points[i - 2], points[i + 1] - points[i - 1]);
  return s;
}

/**
 * Resample a flat x, y polyline to n points evenly spaced along its length (first and last points kept).
 * Even spacing means a marker that steps one index per tick moves at a constant speed.
 */
export function resampleByArc(points: number[], n: number): number[] {
  const m = points.length / 2;
  if (m < 2 || n < 2) return points.slice();
  const cum = new Float64Array(m);
  for (let i = 1; i < m; i++) cum[i] = cum[i - 1] + Math.hypot(points[2 * i] - points[2 * i - 2], points[2 * i + 1] - points[2 * i - 1]);
  const total = cum[m - 1];
  const out: number[] = [];
  let seg = 1;
  for (let k = 0; k < n; k++) {
    const s = (total * k) / (n - 1);
    while (seg < m - 1 && cum[seg] < s) seg++;
    const span = cum[seg] - cum[seg - 1];
    const f = span > 0 ? Math.min(1, Math.max(0, (s - cum[seg - 1]) / span)) : 0;
    out.push(points[2 * seg - 2] + (points[2 * seg] - points[2 * seg - 2]) * f, points[2 * seg - 1] + (points[2 * seg + 1] - points[2 * seg - 1]) * f);
  }
  return out;
}

/** Smallest distance from (px, py) to any segment of the polyline. Segments, not vertices, so coarse steps cannot slip past. */
export function distanceToPolyline(points: number[], px: number, py: number): number {
  const m = points.length / 2;
  if (m === 0) return Infinity;
  if (m === 1) return Math.hypot(points[0] - px, points[1] - py);
  let best = Infinity;
  for (let i = 0; i < m - 1; i++) {
    const ax = points[2 * i], ay = points[2 * i + 1], bx = points[2 * i + 2], by = points[2 * i + 3];
    const ex = bx - ax, ey = by - ay, len2 = ex * ex + ey * ey;
    const t = len2 > 0 ? Math.min(1, Math.max(0, ((px - ax) * ex + (py - ay) * ey) / len2)) : 0;
    const d = Math.hypot(ax + ex * t - px, ay + ey * t - py);
    if (d < best) best = d;
  }
  return best;
}

/** y of the path where it first crosses x = x (after the start), or null if it never does. */
export function crossingY(points: number[], x: number): number | null {
  for (let i = 0; i < points.length / 2 - 1; i++) {
    const ax = points[2 * i], bx = points[2 * i + 2];
    if ((ax - x) * (bx - x) <= 0 && ax !== bx) {
      const f = (x - ax) / (bx - ax);
      return points[2 * i + 1] + (points[2 * i + 3] - points[2 * i + 1]) * f;
    }
  }
  return null;
}

/** Rotate a 2D path (x, y) about the x axis by `angle` into 3D: y -> (y cos, y sin). Returns flat x, y, z. */
export function rotateAboutX(points2d: number[], angle: number): Float32Array {
  const n = points2d.length / 2, out = new Float32Array(n * 3), c = Math.cos(angle), s = Math.sin(angle);
  for (let i = 0; i < n; i++) { out[3 * i] = points2d[2 * i]; out[3 * i + 1] = points2d[2 * i + 1] * c; out[3 * i + 2] = points2d[2 * i + 1] * s; }
  return out;
}

/** Interpolated point at fractional index `x` along a flat xyz polyline, written into `out` at `o`. */
export function sampleXYZ(pts: ArrayLike<number>, n: number, x: number, out: Float32Array, o: number): void {
  const xi = Math.min(Math.max(x, 0), n - 1), j = Math.min(n - 2, Math.floor(xi)), f = xi - j;
  for (let k = 0; k < 3; k++) out[o + k] = pts[3 * j + k] + (pts[3 * j + 3 + k] - pts[3 * j + k]) * f;
}

export const smoothstep = (a: number, b: number, x: number) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/** The offsets (gy, gz) of the light lattice: a square grid, spacing 0.75, inside radius 5.3, skipping rays that would start inside b = 1.08. */
export function latticeOffsets(spacing = 0.75, radius = 5.3, bMin = 1.08): [number, number][] {
  const out: [number, number][] = [];
  const nMax = Math.floor(radius / spacing);
  for (let i = -nMax; i <= nMax; i++) for (let j = -nMax; j <= nMax; j++) {
    const gy = i * spacing, gz = j * spacing, b = Math.hypot(gy, gz);
    if (b < bMin || b > radius) continue;
    out.push([gy, gz]);
  }
  return out;
}

/* ---------- the shot ---------- */

export type ShotVerdict = 'hit' | 'sun' | 'newton-hits' | 'too-bent' | 'too-high';

export interface Shot {
  b: number;
  einstein: Geodesic;
  newton: Geodesic;
  /** closest approach to Earth's telescope, Sun-radii */
  missEinstein: number;
  missNewton: number;
  hit: boolean;
  newtonHit: boolean;
  verdict: ShotVerdict;
}

/** Fire the ray for both physics and judge it against Earth. */
export function evaluateShot(b: number): Shot {
  const einstein = nullGeodesic(b, 'einstein');
  const newton = nullGeodesic(b, 'newton');
  const missEinstein = distanceToPolyline(einstein.points, EARTH.x, EARTH.y);
  const missNewton = distanceToPolyline(newton.points, EARTH.x, EARTH.y);
  const hit = missEinstein < HIT_RADIUS;
  const newtonHit = missNewton < HIT_RADIUS;
  let verdict: ShotVerdict;
  if (hit) verdict = 'hit';
  else if (einstein.fate === 'sun') verdict = 'sun';
  else if (newtonHit) verdict = 'newton-hits';
  else {
    const y = crossingY(einstein.points, EARTH.x);
    verdict = y !== null && y < EARTH.y ? 'too-bent' : 'too-high';
  }
  return { b, einstein, newton, missEinstein, missNewton, hit, newtonHit, verdict };
}

/* ---------- the tick field ---------- */

/** Rate of a clock held still at radius r, against a clock far away. */
export function clockRate(r: number): number { return Math.sqrt(Math.max(0, 1 - RS / r)); }

export const TICK_R_MIN = 1.2;

/** 0 where time runs at Earth's rate, 1 at the inner edge of the field, bent so the gradient is visible. */
export function slowness(r: number): number {
  const ref = 1 - clockRate(TICK_R_MIN);
  return Math.pow((1 - clockRate(r)) / ref, 0.6);
}

/** blue at 0, gold at 0.55, red at 1 */
export function tickColor(slow: number): [number, number, number] {
  const stops: [number, [number, number, number]][] = [[0, [0.3, 0.55, 1.0]], [0.55, [1.0, 0.72, 0.3]], [1, [1.0, 0.1, 0.04]]];
  const t = Math.min(1, Math.max(0, slow));
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i][0]) {
      const s = (t - stops[i - 1][0]) / (stops[i][0] - stops[i - 1][0]), a = stops[i - 1][1], b = stops[i][1];
      return [a[0] + (b[0] - a[0]) * s, a[1] + (b[1] - a[1]) * s, a[2] + (b[2] - a[2]) * s];
    }
  }
  return stops[stops.length - 1][1];
}

/** small deterministic generator so the sky and the field are the same every visit */
export function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => { s = (s + 0x6d2b79f5) >>> 0; let t = s; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

export interface TickFieldData {
  count: number;
  position: Float32Array;
  color: Float32Array;
  /** pulse rate: the clock rate to the 8th power, exaggerated so the gradient shows */
  rate: Float32Array;
  phase: Float32Array;
  size: Float32Array;
}

/** Grid points with step 1 inside +-5, skipping r < 1.2 and |z| > 4.6. */
export function buildTickFieldData(step = 1.0, extent = 5.0, sizeWorld = 0.085, zMax = 4.6, seed = 11): TickFieldData {
  const rng = makeRng(seed);
  const pos: number[] = [], col: number[] = [], rate: number[] = [], phase: number[] = [], size: number[] = [];
  const n = Math.round(extent / step);
  for (let i = -n; i <= n; i++) for (let j = -n; j <= n; j++) for (let k = -n; k <= n; k++) {
    const x = i * step, y = j * step, z = k * step;
    if (Math.abs(z) > zMax) continue;
    const r = Math.hypot(x, y, z);
    if (r < TICK_R_MIN) continue;
    const slow = slowness(r), c = tickColor(slow);
    pos.push(x, y, z); col.push(c[0], c[1], c[2]);
    rate.push(Math.pow(clockRate(r), 8)); phase.push(rng() * Math.PI * 2); size.push(sizeWorld * (1 + 0.6 * slow));
  }
  return {
    count: rate.length,
    position: new Float32Array(pos), color: new Float32Array(col), rate: new Float32Array(rate), phase: new Float32Array(phase), size: new Float32Array(size)
  };
}
